import React from 'react';
import { Box, Grid, Typography, alpha } from '@mui/material';
import { QUADRANTS, getPotential, getQuadrant } from '../../utils/platformKpi';
import { Section } from './common';

// Rows = potential (high/medium on top), columns = usage (low, high) — design §3.5.
const LAYOUT = [
  ['priority', 'grow'],
  ['low_touch', 'steady'],
];

/** Usage × Potential priority grid. */
const PriorityGrid = ({ centers, source, onSelectQuadrant, sx }) => {
  const known = centers.filter((c) => getPotential(c) != null);
  const unknown = centers.length - known.length;
  const groups = { priority: [], grow: [], low_touch: [], steady: [] };
  known.forEach((c) => groups[getQuadrant(c)].push(c));

  return (
    <Section title="Who needs attention" source={source} sx={sx}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', width: 18 }}>
          {['Big business', 'Small business'].map((l) => (
            <Typography key={l} sx={{ fontSize: '0.65rem', color: 'text.secondary', writingMode: 'vertical-rl', transform: 'rotate(180deg)', textAlign: 'center' }}>
              {l}
            </Typography>
          ))}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Grid container spacing={1}>
            {LAYOUT.flat().map((key) => {
              const q = QUADRANTS[key];
              const list = groups[key];
              return (
                <Grid item xs={6} key={key}>
                  <Box
                    data-testid={`quadrant-${key}`}
                    onClick={() => onSelectQuadrant(key)}
                    sx={{
                      p: 1.25,
                      height: '100%',
                      minHeight: 108,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: 1,
                      borderColor: (t) => (q.tint ? alpha(t.palette.primary.main, q.tint * 3) : t.palette.divider),
                      bgcolor: (t) => (q.tint ? alpha(t.palette.primary.main, q.tint) : 'transparent'),
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography data-testid={`quadrant-${key}-count`} sx={{ fontSize: '1.375rem', fontWeight: 700, color: q.tint ? 'primary.main' : 'text.secondary' }}>
                        {list.length}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{q.label}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>{q.action}</Typography>
                    <Typography sx={{ fontSize: '0.72rem' }} noWrap>
                      {list.slice(0, 3).map((c) => c.name).join(', ')}{list.length > 3 ? ` +${list.length - 3}` : ''}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 0.5 }}>
            {['Low usage', 'High usage'].map((l) => (
              <Typography key={l} sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{l}</Typography>
            ))}
          </Box>
          {unknown > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
              Potential unknown ({unknown}) — client and employee counts not available
            </Typography>
          )}
        </Box>
      </Box>
    </Section>
  );
};

export default PriorityGrid;
